<template>
  <!-- No select-all listener: with `selectAll` unset, PrimeVue's toggle-all goes through
       updateModel and emits `change` like any other pick. That rests on the prop's runtime default,
       `null`; PrimeVue's docs give `false`, under which toggle-all would wait for a listener. -->
  <MultiSelect
    ref="select"
    v-model="model"
    :options
    optionLabel="label"
    optionValue="value"
    :filterFields="FILTER_FIELDS"
    :placeholder
    class="w-100"
    filter
    appendTo="body"
    :focusOnHover="false"
    :overlayStyle="hasGroups ? GROUPED_OVERLAY_STYLE : undefined"
    :scrollHeight="listHeight"
    :virtualScrollerOptions="VIRTUAL_SCROLLER_OPTIONS"
    @change="$emit('change')"
    @filter="searchQuery = $event.value"
  >
    <template #filtericon>
      <font-awesome-icon
        icon="search"
        class="text-muted pv-multiselect-filter-icon"
      />
    </template>
    <template v-if="hasGroups" #header>
      <div class="device-group-multi-select__header">
        <template v-if="groupRows.length > 0">
          <div class="device-group-multi-select__label">
            {{ groupsTitle ?? $t("device_groups.filter.groups") }}
          </div>
          <DeviceGroupChecklist :rows="groupRows" @toggle="toggleOptionGroup" />
        </template>
        <div class="device-group-multi-select__label">
          {{ $t("device_groups.filter.devices") }}
        </div>
      </div>
    </template>
    <template #value="{ value, placeholder: shownPlaceholder }">
      <span
        v-if="value && value.length"
        class="fw-bold"
        :class="tone && `text-${tone}`"
      >
        {{ selectionLabel }}
      </span>
      <span v-else>{{ shownPlaceholder }}</span>
    </template>
    <template #option="{ option }">
      <div class="d-flex w-100 gap-2 align-items-center">
        <TechnologyIcon
          v-if="option.technology"
          :key="option.technology"
          :technology="option.technology"
          iconClass="fa-fw flex-shrink-0"
        />
        <span class="text-truncate">{{ option.label }}</span>
        <span
          v-if="option.description && option.description !== option.label"
          class="text-body-secondary device-group-multi-select__secondary text-truncate"
        >
          {{ option.description }}
        </span>
        <span
          v-if="groupLabelByValue.has(option.value)"
          class="ms-auto text-body-secondary device-group-multi-select__secondary device-group-multi-select__option-group text-truncate"
        >
          {{ groupLabelByValue.get(option.value) }}
        </span>
      </div>
    </template>
  </MultiSelect>
</template>

<script setup lang="ts">
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import DeviceGroupChecklist, {
  type DeviceGroupChecklistRow,
} from "@/dashboard/production_strategy/device_groups/DeviceGroupChecklist.vue";
import { groupSelectionState, toggleGroup } from "@/utils/groupSelection";
import { FilterMatchMode, FilterService } from "@primevue/core/api";
import MultiSelect from "primevue/multiselect";
import type { VirtualScrollerMethods } from "primevue/virtualscroller";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

export interface DeviceSelectOption {
  label: string;
  value: string;
  /** A second identifier, e.g. the EIC, shown beside the label and matched by the search. */
  description?: string;
  /** A TechnologyIcon technology (e.g. "WIND"), shown before the label. */
  technology?: string;
}

/** A device group in the dropdown: ticking it selects these option values. */
export interface DeviceSelectGroup {
  label: string;
  values: string[];
}

/**
 * Picks devices, with a Groups section of tri-state checkboxes heading the options when `groups`
 * are given: ticking a group selects those of its values that are among the options, so the model
 * stays plain values. A partly selected group counts its selected values ("1 of 2 devices"). Each
 * option names its group, the search narrows the groups as well as the
 * options, and the field reads one group's name, "N groups" or "N devices".
 *
 * Used both as a form field and as a DataTable column filter: there, add `class="p-column-filter"`,
 * bind the slot's `filterModel.value` with `v-model` and call its `filterCallback()` on `change`.
 */
const props = withDefaults(
  defineProps<{
    options: DeviceSelectOption[];
    groups?: DeviceSelectGroup[];
    placeholder?: string;
    /** Heads the group checkboxes; "Groups" by default. */
    groupsTitle?: string;
    /** Colours the selection label, as the column filters do (danger flags an unapplied filter). */
    tone?: "primary" | "danger";
  }>(),
  {
    groups: () => [],
    placeholder: undefined,
    groupsTitle: undefined,
    tone: undefined,
  }
);

const emit = defineEmits<{
  change: [];
}>();

const model = defineModel<string[] | null>();

const { t } = useI18n();

const FILTER_FIELDS = ["label", "description"];

// With groups the overlay grows past its 300px cap, so the options are not squeezed to two rows
// under the Groups section and the search box.
const GROUPED_OVERLAY_STYLE = { maxHeight: "min(600px, 60vh)" };

// PERF: a fleet can list hundreds of devices, each a PrimeVue Checkbox plus our slot; virtualised, the
// overlay renders only the rows in view (366 options: ~155 ms -> ~40 ms to open). The price is fixed
// geometry: MultiSelect pins every option row to OPTION_HEIGHT inline (just above
// .pv-multiselect-option's natural ~35.6px), and the other two mirror primevue-unstyled.css:
// LIST_PADDING is .pv-multiselect-list's vertical padding (VirtualScroller adds it to its spacer),
// EMPTY_MESSAGE_HEIGHT .pv-multiselect-empty's height. The scroller is `contain: strict` around an
// absolutely positioned list, so it needs an explicit height; it gets its final one before it opens,
// as MultiSelect places (and flips) the overlay from it.
const OPTION_HEIGHT = 36;
const LIST_PADDING = 8;
const EMPTY_MESSAGE_HEIGHT = 44;
const VIRTUAL_SCROLLER_OPTIONS = { itemSize: OPTION_HEIGHT };

/**
 * Undocumented MultiSelect internals (PrimeVue 4.5): its filtered options, and its VirtualScroller,
 * set only while the overlay is open. PrimeVue documents neither, so the spec pins both.
 */
type MultiSelectInternals = {
  visibleOptions: DeviceSelectOption[];
  virtualScroller: VirtualScrollerMethods | null;
};
const select = useTemplateRef<MultiSelectInternals>("select");

// PrimeVue's search text, so the search narrows the groups as well as the options.
const searchQuery = ref("");

const hasGroups = computed(() => props.groups.length > 0);

// The options PrimeVue lists for the search, read from PrimeVue rather than filtered again.
const shownOptions = computed(
  () => select.value?.visibleOptions ?? props.options
);

// Rows shown before the list scrolls; with groups the overlay has room for more.
const maxRows = computed(() => (hasGroups.value ? 8 : 6));

const listHeight = computed(() => {
  const rows = Math.min(shownOptions.value.length, maxRows.value);
  return `${(rows ? rows * OPTION_HEIGHT : EMPTY_MESSAGE_HEIGHT) + LIST_PADDING}px`;
});

const optionValues = computed(() =>
  props.options.map((option) => option.value)
);

// Keyed by the group's index in `groups`: labels from different companies' groups can repeat.
const groupRows = computed<DeviceGroupChecklistRow[]>(() => {
  const available = new Set(optionValues.value);
  const selected = new Set(model.value ?? []);
  const rows = props.groups.map((group, index) => {
    const state = groupSelectionState(
      group.values,
      optionValues.value,
      model.value ?? []
    );
    return {
      key: index,
      label: group.label,
      countLabel:
        state === "partial"
          ? t("device_groups.picker.group_partial_count", {
              selected: group.values.filter((value) => selected.has(value))
                .length,
              count: group.values.length,
            })
          : t(
              "device_groups.devices_count",
              { count: group.values.length },
              group.values.length
            ),
      state,
      disabled: !group.values.some((value) => available.has(value)),
    };
  });
  // Matched by the FilterService call PrimeVue filters the options with, so one search narrows both alike.
  return FilterService.filter(
    rows,
    ["label"],
    searchQuery.value,
    FilterMatchMode.CONTAINS
  );
});

const groupLabelByValue = computed(() => {
  const labels = new Map<string, string>();
  for (const group of props.groups) {
    for (const value of group.values) labels.set(value, group.label);
  }
  return labels;
});

/**
 * A group's name when the selection is exactly one whole group, a count of whole groups when it is
 * several, the option's label when it is one device, and a device count otherwise.
 */
const selectionLabel = computed(() => {
  const selected = model.value ?? [];
  const selectedSet = new Set(selected);
  const wholeGroups = props.groups.filter(
    (group) =>
      group.values.length > 0 &&
      group.values.every((value) => selectedSet.has(value))
  );
  const covered = new Set(wholeGroups.flatMap((group) => group.values));
  if (wholeGroups.length === 1 && covered.size === selectedSet.size) {
    return wholeGroups[0].label;
  }
  if (wholeGroups.length > 1 && covered.size === selectedSet.size) {
    return t(
      "device_groups.filter.n_groups",
      { count: wholeGroups.length },
      wholeGroups.length
    );
  }
  if (selected.length === 1) {
    return (
      props.options.find((option) => option.value === selected[0])?.label ??
      selected[0]
    );
  }
  return t(
    "device_groups.devices_count",
    { count: selected.length },
    selected.length
  );
});

const toggleOptionGroup = (key: number) => {
  const group = props.groups[key];
  // The new selection, read from here: `model.value` keeps the old one until the parent re-renders.
  const selection = toggleGroup(
    group.values,
    optionValues.value,
    model.value ?? []
  );
  model.value = selection;
  emit("change");
  if (group.values.some((value) => selection.includes(value))) {
    // Bring the group's first option to the top of the list (scrollToIndex keeps the list at the top
    // when it is among the first rows), so ticking a group shows what it ticked.
    const values = new Set(group.values);
    select.value?.virtualScroller?.scrollToIndex(
      shownOptions.value.findIndex((option) => values.has(option.value))
    );
  }
};
</script>

<style scoped lang="scss">
// Sits in the overlay's fixed-height flex column: keep the group rows from being squeezed away.
.device-group-multi-select__header {
  flex-shrink: 0;
}

.device-group-multi-select__label {
  padding: 8px 12px 4px;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--phoenix-quaternary-color);
}

.device-group-multi-select__secondary {
  font-size: 0.72rem;
}

.device-group-multi-select__option-group {
  max-width: 50%;
}
</style>
